import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import HeroSection from "@/components/layout/HeroSection";
import { PremiumCard, IconWithGlow } from "@/components/ui/premium-card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calculator, Shield, Award, ShieldCheck, Sparkles } from "lucide-react";
import heroImage from "@/assets/vergleichsrechner-datacenter.jpg";
const VergleichsrechnerCyber = () => {
  return <>
      <Helmet>
        <title>Cyberversicherung Vergleichsrechner | Bis zu 40% sparen</title>
        <meta name="description" content="Vergleichen Sie 15+ Top-Versicherer in 3 Minuten. Speziell für Arztpraxen. 100% kostenlos, unabhängig & DSGVO-konform." />
        <link rel="canonical" href="https://praxis-versichert.de/cyberversicherung/vergleichsrechner" />
      </Helmet>

      <Navigation />
      
      <PageBreadcrumbs items={[{
      label: "Home",
      path: "/"
    }, {
      label: "Cyberversicherung",
      path: "/cyberversicherung"
    }, {
      label: "Vergleichsrechner"
    }]} />

      {/* HERO SECTION */}
      <HeroSection
        image={heroImage}
        alt="Moderner Serverraum mit IT-Infrastruktur für Cyberversicherung"
        gradientPreset="none"
        imageOpacity={30}
        overlayColor="background"
        className="py-12 sm:py-16 lg:py-20 px-4"
      >
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="text-center space-y-6 sm:space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-2">
              <Sparkles className="w-4 h-4 text-accent" />
              <span className="text-xs sm:text-sm font-medium text-accent text-center">
                Deutschlands führender Cyberversicherungs-Vergleich
              </span>
            </div>

            {/* H1 Headline */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-accent via-accent/80 to-accent/60 bg-clip-text text-transparent">
                Vergleichsrechner
              </span>
              <br />
              <span className="text-foreground">
                für Cyberversicherung
              </span>
            </h1>

            {/* Subline */}
            <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-muted-foreground px-4">
              15+ Top-Versicherer • 100% kostenlos • Unabhängige Beratung
            </p>

            {/* 3 Trust Icons */}
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 sm:gap-6 lg:gap-8 pt-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                <span className="text-xs sm:text-sm text-foreground">2.847+ Ärzte vertrauen uns</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Award className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                <span className="text-xs sm:text-sm text-foreground">TÜV-geprüfte Beratung</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                <span className="text-xs sm:text-sm text-foreground">DSGVO-konform</span>
              </div>
            </div>
          </motion.div>
        </div>
      </HeroSection>

      {/* CTA SECTION - außerhalb Hero */}
      <section className="py-12 sm:py-16 lg:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <PremiumCard hoverGradient="accent" className="p-6 sm:p-8 lg:p-10 xl:p-12">
            <div className="text-center space-y-6 sm:space-y-8">
              {/* Icon mit Glow */}
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-accent/20 blur-2xl rounded-full animate-pulse" />
                <IconWithGlow icon={Calculator} size="lg" />
              </div>

              {/* Beschreibungstext */}
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
                Über 15 Top-Versicherer im direkten Vergleich – kostenlos, unverbindlich & DSGVO-konform
              </p>

              {/* 2 Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-4">
                <a href="https://kunden-portal.cyberdirekt.de/?ref=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY3NTA3YmQwMTlmM2IyYjU0OGM5NGEzYyIsImZpcnN0bmFtZSI6IlRob21hcyIsImxhc3RuYW1lIjoiRGV3ZWluIn0.X_v1hAubU2FypQ_89Nk8_9NFRhIrRDBv0Cmv4egtB30%2F#/versicherung/1a/ihr-unternehmen/branche" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base font-semibold">
                    Vergleichsrechner starten
                  </Button>
                </a>
                <Link to="/termin-buchen">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base font-semibold">
                    Kostenlose Beratung
                  </Button>
                </Link>
              </div>

              {/* Trust Badge */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-xs text-muted-foreground pt-4 border-t border-accent/10">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-accent" />
                  <span className="text-center">SSL-verschlüsselt • 100% kostenlos</span>
                </div>
                <span className="hidden sm:inline">•</span>
                <span className="text-center">Keine Registrierung erforderlich</span>
              </div>
            </div>
          </PremiumCard>
        </div>
      </section>

      <Footer />

      {/* STICKY MOBILE CTA - vereinfacht */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-3 pb-safe">
        <div className="backdrop-blur-xl bg-background/95 border-t-2 border-accent/40 shadow-[0_-4px_20px_rgba(0,0,0,0.3)] rounded-t-2xl sm:rounded-t-3xl p-3 sm:p-4">
          <a href="https://kunden-portal.cyberdirekt.de/?ref=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY3NTA3YmQwMTlmM2IyYjU0OGM5NGEzYyIsImZpcnN0bmFtZSI6IlRob21hcyIsImxhc3RuYW1lIjoiRGV3ZWluIn0.X_v1hAubU2FypQ_89Nk8_9NFRhIrRDBv0Cmv4egtB30%2F#/versicherung/1a/ihr-unternehmen/branche" target="_blank" rel="noopener noreferrer" className="w-full">
            <Button variant="outline" size="lg" className="w-full h-12 sm:h-14 text-sm sm:text-base font-semibold">
              <Calculator className="w-5 h-5 mr-2" />
              Vergleichsrechner starten
            </Button>
          </a>
        </div>
      </div>
    </>;
};
export default VergleichsrechnerCyber;